import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { securePut } from '../../config/api';

const MaintenanceAuthPopup = ({ open, onConfirm, onCancel, technicianName, onSuccess, onError }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState(['', '', '', '']);
  const [error, setError] = useState(false);
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    if (open && inputRefs[0].current) {
      inputRefs[0].current.focus();
    }
  }, [open]);

  const handleChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(false);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...code];
    for (let i = 0; i < pastedData.length && i < 4; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);
    
    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex((digit, idx) => !digit && idx < 4);
    if (nextEmptyIndex !== -1) {
      inputRefs[nextEmptyIndex].current?.focus();
    } else {
      inputRefs[3].current?.focus();
    }
  };

  const handleConfirm = async () => {
    const enteredCode = code.join('');
    if (enteredCode.length !== 4) {
      setError(true);
      return;
    }

    try {
      await securePut(
        '/users/maintenance-status',
        {
          secretCode: enteredCode,
        },
        (message, severity) => {
          if (severity === 'error' && onError) {
            onError(message);
          }
        }
      );

      onConfirm(enteredCode);
      if (onSuccess) {
        onSuccess();
      }
      setCode(['', '', '', '']);
      setError(false);
    } catch (error) {
      console.error('Error setting maintenance status:', error);
      setError(true);
      if (onError) {
        onError('Erreur lors de l\'activation du mode entretien');
      }
    }
  };

  const handleClose = () => {
    setCode(['', '', '', '']);
    setError(false);
    onCancel();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1100]">
      <div className="bg-[#F9FAFC] rounded-[12px] w-[580px] h-[330px] p-8">
        {/* Title */}
        <h2 className="text-[24px] font-semibold text-[#38485C] text-center mb-2">
          {t('kanban.auth_required')}
        </h2>

        {/* Instructions */}
        <p className="text-[16px] font-semibold text-[#4D5B68] text-center mb-8">
          {t('kanban.auth_instructions')}
        </p>

        {/* Code Input Fields */}
        <div className="flex justify-center gap-4 mb-2">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              className={`w-16 h-16 text-center text-[#38485C] text-[24px] font-semibold border rounded-[12px] focus:outline-none focus:ring-2 focus:ring-primary ${
                error ? 'border-[#EF466F]' : 'border-[#E3E3E3]'
              }`}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <p className="text-[#EF466F] text-[16px] text-center mb-4">
            {t('kanban.code_incorrect')}
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 text-[#36495C] text-[18px] font-semibold"
          >
            {t('actions.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="flex-1 py-3 bg-primary text-white text-[18px] rounded-[12px] font-semibold shadow-[0px_4px_10px_rgba(190,190,190,0.5)]"
          >
            {t('actions.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceAuthPopup;

